/**
 * §9.7 Enterprise Consulting — 5 advisory focus areas + CTA card.
 */

import React from 'react'
import {
  ClipboardList, Building2, Workflow, UsersRound, TrendingUp,
  Sparkles, ArrowRight, Compass,
} from 'lucide-react'
import {
  Container, Eyebrow, SectionHeading, GradientButton, GhostButton,
} from '@/components/landing/design-system'
import { CONSULTING } from '../data'
import { TONE } from '../_shared'

const ICONS = { ClipboardList, Building2, Workflow, UsersRound, TrendingUp }

export default function Consulting() {
  return (
    <section id="consulting" className="relative py-24 md:py-28 scroll-mt-24">
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Enterprise Consulting</Eyebrow>
          <SectionHeading align="center"
            title={<>Advisory built for{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">complex deployments.</span></>}
            subtitle="Larger rollouts, multi-brand estates and enterprise transformation programmes get named advisors who architect, plan and deliver alongside your team."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left — 5 focus areas */}
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CONSULTING.map((c) => {
              const tone = TONE[c.tone] || TONE.orange
              const Icon = ICONS[c.iconKey] || Sparkles
              return (
                <div key={c.id} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 backdrop-blur-xl overflow-hidden flex flex-col gap-3">
                  <div className="pointer-events-none absolute inset-0 opacity-40"
                    style={{ background: `radial-gradient(80% 45% at 20% 0%, rgba(${tone.rgb},0.12), transparent 70%)` }} />
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl border"
                    style={{ borderColor: `rgba(${tone.rgb},0.35)`, background: `rgba(${tone.rgb},0.08)`, color: tone.hex }}>
                    <Icon strokeWidth={1.8} style={{ width: 18, height: 18 }} />
                  </span>
                  <div className="relative">
                    <h4 className="text-[14.5px] font-semibold text-white leading-tight">{c.label}</h4>
                    <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-1.5">{c.body}</p>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Right — CTA card */}
          <div className="lg:col-span-4">
            <div className="relative rounded-3xl border border-cyan-400/30 bg-gradient-to-b from-cyan-400/[0.06] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden flex flex-col gap-4 h-full">
              <div className="pointer-events-none absolute inset-0 opacity-70"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(34,211,238,0.12), transparent 70%)' }} />
              <div className="relative flex items-center gap-2">
                <Compass className="h-4 w-4 text-cyan-400" />
                <span className="text-[10.5px] font-mono uppercase tracking-wider text-cyan-400">Enterprise desk</span>
              </div>
              <div className="relative text-[15px] font-semibold text-white leading-snug">
                Speak with an advisor who has already built platforms like the one you&apos;re planning.
              </div>
              <ul className="relative flex flex-col gap-1.5 text-[12.5px] text-zinc-300">
                <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-cyan-400" /> 45-minute discovery workshop</li>
                <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-cyan-400" /> Written architecture recommendation</li>
                <li className="flex items-center gap-2"><span className="h-1 w-1 rounded-full bg-cyan-400" /> Bespoke SOW and rollout plan</li>
              </ul>
              <div className="relative mt-auto flex flex-col gap-2">
                <GradientButton href="#sales" className="w-full justify-center">
                  <Compass className="h-4 w-4" /> Speak with an expert
                </GradientButton>
                <GhostButton href="#demo" trailingIcon className="w-full justify-center">
                  Book a demo first <ArrowRight className="h-3 w-3" />
                </GhostButton>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
